import { http, pagination, id } from 'utils'
import { concat, merge, set, findIndex } from 'lodash'
import { SET_AGENCIES, SET_META, ADD_AGENCIES, SET_AGENCY, RESET_AGENCY, SET_PROPERTY, REMOVE_AGENCY_COORDINATOR, ADD_AGENCY_COORDINATOR } from 'vx/mutations'

export const initialAgencyState = () => ({
  name: null,
  abbreviation: null,
  enabled: true,
  coordinators: []
})

// Agencies state.
const state = {
  agency: initialAgencyState(),
  agencies: [],
  meta: null
}

// Agencies state getters.
const getters = {}

// Agencies actions
const actions = {
  /**
   * Get all agencies.
   *
   * @return Promise
   */
  async getAgencies ({ commit }, params = {}) {
    commit(SET_AGENCIES, [])
    commit(SET_META, null)

    const response = await http.get('agencies', { params })
    commit(SET_AGENCIES, response.data.data)
    commit(SET_META, response.data.meta)

    return response
  },

  /**
   * Get next lot of agencies
   *
   * not being used at the moment, but keeping it in case they decide to add
   * pagination in the future
   *
   * @param {*} params
   */
  async getAgenciesForNextPage ({ commit, state }, params = {}) {
    if (pagination(state.meta)) {
      return Promise.reject(new Error('No more results.'))
    }

    params.page = state.meta.current_page + 1

    const response = await http.get('agencies', { params })

    commit(ADD_AGENCIES, response.data.data)
    commit(SET_META, response.data.meta)
  },

  /**
   * Get a agency by id
   *
   * @param integer id
   */
  async getAgency ({ commit }, id) {
    commit(RESET_AGENCY)

    const response = await http.get(`agencies/${id}`)

    commit(SET_AGENCY, response.data.data)

    return response
  },

  /**
   * Create new agency and set on state
   *
   * @param object payload
   */
  async storeAgency ({ commit }, payload) {
    const response = await http.post(`agencies`, payload)

    commit(SET_AGENCY, response.data.data)

    return response
  },

  /**
   * Update an existing agency
   *
   * @param object|int agency
   * @param oject payload
   */
  updateAgency ({ state }, { agency, payload }) {
    return http.put(`agencies/${id(agency)}`, payload)
  },

  /**
   * Add coordinator to agency
   *
   * @param object|int agency
   * @param object|int coordinator
   * @param oject payload
   */
  async addCoordinator ({ commit }, { agency, coordinator }) {
    const response = await http.post(`agencies/${id(agency)}/coordinators/${id(coordinator)}`)

    commit(ADD_AGENCY_COORDINATOR, response.data.data)

    return response
  },

  /**
   * Remove coordinator from agency
   *
   * @param object|int agency
   * @param object|int coordinator
   * @param oject payload
   */
  async removeCoordinator ({ commit }, { agency, coordinator }) {
    const response = await http.delete(`agencies/${id(agency)}/coordinators/${id(coordinator)}`)

    commit(REMOVE_AGENCY_COORDINATOR, coordinator.id)

    return response
  },

  /**
   * Get an agencies coordinator log.
   *
   * @return Promise
   */
  async getCoordinatorLog ({ commit }, agency) {
    return http.get(`/agencies/${id(agency)}/coordinators-log`, { responseType: 'blob' })
  },

  /**
   * Reset agency to initial state
   */
  resetAgency ({ commit }) {
    commit(RESET_AGENCY)
  },

  /**
   * Set a property on a agency
   *
   * @param object payload
   */
  setAgencyProperty ({ commit }, payload) {
    commit(SET_PROPERTY, payload)
  }
}

// Agencies state mutations.
const mutations = {
  [SET_AGENCIES] (state, agencies) {
    state.agencies = agencies
  },

  [SET_META] (state, meta) {
    state.meta = meta
  },

  [ADD_AGENCIES] (state, agencies) {
    state.agencies = concat(state.agencies, agencies)
  },

  [SET_AGENCY] (state, agency) {
    state.agency = merge(initialAgencyState(), agency)
  },

  [RESET_AGENCY] (state, agency) {
    state.agency = initialAgencyState()
  },

  [SET_PROPERTY] (state, { property, value }) {
    set(state.agency, property, value)
  },

  [ADD_AGENCY_COORDINATOR] (state, coordinator) {
    let index = findIndex(state.agency.coordinators, existing => existing.id === coordinator.id)

    if (index === -1) {
      state.agency.coordinators.unshift(coordinator)
    }
  },

  /**
   * Remove a coordinator from the agency's coordinators
   */
  [REMOVE_AGENCY_COORDINATOR] (state, id) {
    let index = findIndex(state.agency.coordinators, coordinator => coordinator.id === id)

    if (index !== -1) {
      state.agency.coordinators.splice(index, 1)
    }
  }
}

// Export our state, getters, actions, and mutations.
export default {
  namespaced: true, state, getters, actions, mutations
}
